/**
 * Service worker registration (production builds only; from this module – no inline script, CSP
 * `script-src 'self'`). A new version waits until the user accepts the update toast; then the
 * page reloads once the new worker controls it. Also shows an offline hint.
 */
import { t } from '../i18n';
import { dismissToast, toast } from '../services/notify';

export const SW_URL = '/sw.js';
const UPDATE_CHECK_MS = 60 * 60 * 1000;

function offerUpdate(worker: ServiceWorker): void {
  toast(t('app.updateAvailable'), 'info', {
    timeout: 0,
    action: { label: t('app.updateReload'), run: () => worker.postMessage({ type: 'SKIP_WAITING' }) },
  });
}

function watchOnline(): void {
  let id: number | undefined;
  window.addEventListener('offline', () => (id = toast(t('app.offline'), 'warning', { timeout: 0 })));
  window.addEventListener('online', () => id !== undefined && dismissToast(id));
}

export function registerServiceWorker(): void {
  watchOnline();
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  let reloading = false;
  sw.addEventListener('controllerchange', () => {
    // first install (clients.claim) is no update – only reload when a previous worker was replaced
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });
  const register = async () => {
    try {
      const reg = await sw.register(SW_URL, { scope: '/' });
      if (reg.waiting && sw.controller) offerUpdate(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && sw.controller) offerUpdate(w);
        });
      });
      setInterval(() => void reg.update().catch(() => {}), UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void reg.update().catch(() => {}));
    } catch (e) {
      console.warn('service worker registration failed', e);
    }
  };
  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => void register(), { once: true });
}
